import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { GetDataError } from 'restful-react'
import { StringParam } from 'use-query-params'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import {
  useKeepTruckinExchangeAuthCode,
  KeepTruckinAuthCode,
  KeepTruckinExchangeAuthCodeResponse,
  useKeepTruckinImportTrucks,
} from 'apps/fleet-planner/types'
import { getRestfulProps } from 'utils/getRestfulProps'
import { useQueryParams } from 'utils/hooks'
import { KeepTruckinImportTrucksRequestBody } from '../types'
import { useFMSRoutes } from './useFMSRoutes'

export interface IPathParams {
  /**
   * Stores carrierId in path param
   */
  carrierId: string
}

export const useKeepTruckingAuthPararms = () => {
  return useQueryParams<KeepTruckinAuthCode>({
    code: StringParam,
    state: StringParam,
    motorCarrierId: StringParam,
  })
}

/**
 * Handles business logic for ELD Integration
 */
export const useELDIntegration = () => {
  const { trackEvent } = useFMSAnalytics()
  const params = useParams<IPathParams>()
  const [queryParams] = useKeepTruckingAuthPararms()
  const history = useFMSRoutes()

  /**
   * Auth token handler
   */
  const {
    cancel: cancelHandler,
    error: authCodeError,
    loading: authCodeLoading,
    mutate: postToken,
  } = useKeepTruckinExchangeAuthCode({})

  const {
    error: importTrucksError,
    loading: importTrucksLoading,
    mutate: importTrucks,
  } = useKeepTruckinImportTrucks({
    ...getRestfulProps(),
  })

  /**
   * Handles response from oAuth API
   */
  const authCallbackHandler = useCallback(async () => {
    const { code, state } = queryParams || {}

    let response: KeepTruckinExchangeAuthCodeResponse | undefined

    try {
      response = await postToken({
        code,
        state,
      })
      trackEvent('ELD', 'KT_AUTH_CALLBACK_SUCCESS')
    } catch (e) {
      trackEvent('ELD', 'KT_AUTH_CALLBACK_FAILED')
    }

    // get relevant carrier from response
    const { motorCarrierId } = response || {}

    if (response && !authCodeError && motorCarrierId) {
      setTimeout(() => {
        history.pushToImportTrucks(motorCarrierId)
      }, 1000)
      return true
    }

    return false
  }, [authCodeError, history, postToken, queryParams])

  /**
   * Generates a click handler for routing user to connect page for carrier
   */
  const createRouteClickHandler = useCallback(
    (carrierId: string | number) => {
      return () => {
        trackEvent('ELD', 'CLICKED_CONNECT_ELD_BUTTON', { carrierId })
        history.pushToConnectCarrier(carrierId)
      }
    },
    [history]
  )

  const importTrucksHandler = useCallback(
    (req: KeepTruckinImportTrucksRequestBody) => {
      return importTrucks(req)
    },
    [importTrucks]
  )

  const loadingStates = useMemo(
    () => ({
      authCodeLoading,
      importTrucksLoading,
    }),
    [authCodeLoading, importTrucksLoading]
  )

  /**
   * Helper to grab loading state
   */
  const getLoadingState = useCallback(
    (keyName: keyof typeof loadingStates): boolean => {
      return loadingStates[keyName]
    },
    [loadingStates]
  )

  const errorStates = useMemo(
    () => ({
      authCodeError,
      importTrucksError,
    }),
    [authCodeError, importTrucksError]
  )

  const getErrorState = useCallback(
    (keyName: keyof typeof errorStates): GetDataError<unknown> | null => {
      return errorStates[keyName]
    },
    [errorStates]
  )

  return {
    cancelHandler,
    authCallbackHandler,
    params,
    createRouteClickHandler,
    importTrucksHandler,
    getLoadingState,
    getErrorState,
  }
}
